import { computed, ref, watch } from 'vue';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import { ORDER_TEMPLATE_MAPPING_FIELDS } from '@/orderTemplateModule/constants/mappingFields';
import { useForms } from '@/corePagesModule/composables/useForm';
import { ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { FormValidateFunctionFactory } from '@/coreDomainsModule/factories/formValidateFunctionFactory';
import { diContainer } from '@/coreContainer/container';
import type { OrderTemplateUpsertRequest } from '@/coreInfrastructureModule/axios/generated/orderManage/api';
import { i18n } from '@/i18n';

/**
 * マッピング行の状態
 */
type MappingFieldState = {
  fieldKey: string;
  label: string;
  startCell: string;
  enabled: boolean;
};

/** セル座標の検証に用いる正規表現 */
const t = i18n.t;
const CELL_PATTERN = /^[A-Z]{1,3}[1-9][0-9]{0,3}$/;
/** セッションストレージに保存するドラフトのキー接頭辞 */
const TEMPLATE_DRAFT_PREFIX = 'orderTemplateDraft:';
/** 1シート上限数フィールドの識別子 */
const MAX_ORDERS_FIELD_NAME = 'maxOrdersPerSheet';
const DUPLICATE_CELL_ERROR = t('同じセルが複数回指定されています。重複しているセルを削除してください。') as string;

/**
 * フォームコンポーザブルの初期化オプション
 */
type UseOrderTemplateFormOptions = {
  route: RouteLocationNormalizedLoaded;
  router: Router;
};

/**
 * 発注テンプレート作成フォームの状態と操作を提供する。
 * @param options - ルーティング情報とナビゲーション操作
 */
export const useOrderTemplateForm = ({ route, router }: UseOrderTemplateFormOptions) => {
  const templateId = ref('');
  const fileOriginalName = ref('');
  const storagePath = ref('');
  const maxOrdersPerSheetInput = ref('20');
  const maxOrdersPerSheet = computed<number | null>(() => {
    const trimmed = maxOrdersPerSheetInput.value.trim();
    if (trimmed === '') {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  });
  const formMessage = ref('');
  const formMessageType = ref<'info' | 'error'>('info');
  const loading = ref(false);
  const initializing = ref(false);

  /**
   * マッピング用のフィールド名を生成する。
   * @param fieldKey - マッピング対象のフィールドキー
   * @returns フィールド名
   */
  const buildMappingFieldName = (fieldKey: string): string => `mapping:${fieldKey}`;

  /**
   * マッピング行の初期状態を生成する。
   * @returns 初期化済みのマッピング配列
   */
  const createDefaultMappingStates = (): MappingFieldState[] =>
    ORDER_TEMPLATE_MAPPING_FIELDS.map((field) => ({
      fieldKey: field.fieldKey,
      label: field.label,
      startCell: '',
      enabled: false,
    }));

  const mappingStates = ref<MappingFieldState[]>(createDefaultMappingStates());

  const formFieldItems = [
    {
      name: MAX_ORDERS_FIELD_NAME,
      rules: [ValidateRule.Required, ValidateRule.HalfWidthNumber, ValidateRule.NegativeNumber] as const,
    },
    ...ORDER_TEMPLATE_MAPPING_FIELDS.map((field) => ({
      name: buildMappingFieldName(field.fieldKey),
      rules: [] as const,
    })),
  ];

  const { fieldState } = useForms(formFieldItems);
  fieldState[MAX_ORDERS_FIELD_NAME].setValue(maxOrdersPerSheetInput.value, false);

  const isEditMode = computed(() => route.name === 'order-template-edit');
  const hasUploadInfo = computed(() => isEditMode.value || Boolean(templateId.value && fileOriginalName.value && storagePath.value));
  const selectedMappings = computed(() => mappingStates.value.filter((field) => field.enabled));
  const isBusy = computed(() => initializing.value || loading.value);

  /**
   * マッピング行に紐づくバリデーションルールを更新する。
   * @param field - 対象のマッピング状態
   */
  const applyMappingFieldRules = (field: MappingFieldState) => {
    const fieldName = buildMappingFieldName(field.fieldKey);
    const target = fieldState[fieldName];
    const rules: ValidateRule[] = field.enabled ? [ValidateRule.Required] : [];
    target.rules = rules as readonly ValidateRule[];
    target.validate = FormValidateFunctionFactory.create(target.rules);
  };

  /**
   * マッピング行のフォーム状態を同期し検証する。
   * @param field - 対象のマッピング状態
   * @param options - 検証オプション
   * @returns バリデーション結果
   */
  const updateMappingFieldState = (
    field: MappingFieldState,
    options: { validateValue?: boolean; enforcePattern?: boolean } = {}
  ): boolean => {
    const { validateValue = false, enforcePattern = false } = options;
    const fieldName = buildMappingFieldName(field.fieldKey);
    const target = fieldState[fieldName];
    applyMappingFieldRules(field);

    if (!field.enabled) {
      target.setValue('', false);
      target.isError.value = false;
      target.errorMessage.value = '';
      return true;
    }

    target.setValue(field.startCell, validateValue);
    if (target.isError.value) {
      target.errorMessage.value = t('出力開始セルを指定してください') as string;
      return false;
    }

    if (enforcePattern && field.startCell !== '' && !CELL_PATTERN.test(field.startCell)) {
      target.isError.value = true;
      target.errorMessage.value = t('セル形式が正しくありません（例：A1, B10, AA10）') as string;
      return false;
    }

    target.isError.value = false;
    target.errorMessage.value = '';
    return true;
  };

  /**
   * 1シートあたりの出力上限値を検証する。
   * @param options - 値設定と検証に関するオプション
   * @returns バリデーション結果
   */
  const validateMaxOrdersPerSheet = (options: { runSetValue?: boolean } = {}): boolean => {
    const { runSetValue = true } = options;
    const target = fieldState[MAX_ORDERS_FIELD_NAME];
    if (runSetValue) {
      target.setValue(maxOrdersPerSheetInput.value, true);
    }

    const numeric = maxOrdersPerSheet.value;

    if (target.isError.value) {
      target.errorMessage.value = t('1以上の整数で入力してください') as string;
      return false;
    }

    if (numeric === null || !Number.isInteger(numeric) || numeric <= 0) {
      target.isError.value = true;
      target.errorMessage.value = t('1以上の整数で入力してください') as string;
      return false;
    }

    if (numeric > 10_000) {
      target.isError.value = true;
      target.errorMessage.value = t('上限は10000までです') as string;
      return false;
    }

    target.isError.value = false;
    target.errorMessage.value = '';
    return true;
  };

  /**
   * 1シートあたりの出力上限数入力を処理する。
   * @param rawValue - ユーザーが入力した文字列
   */
  const handleMaxOrdersPerSheetInput = (rawValue: string) => {
    maxOrdersPerSheetInput.value = rawValue;
    fieldState[MAX_ORDERS_FIELD_NAME].setValue(rawValue, true);
    void validateMaxOrdersPerSheet({ runSetValue: false });
  };

  /**
   * マッピング項目の選択状態を切り替える。
   * @param index - 対象行のインデックス
   * @param enabled - 選択状態
   */
  const handleToggleField = (index: number, enabled: boolean) => {
    const target = mappingStates.value[index];
    target.enabled = enabled;
    if (!enabled) {
      target.startCell = '';
    }
    updateMappingFieldState(target, { validateValue: false, enforcePattern: false });
  };

  /**
   * セル座標の入力を処理する。
   * @param index - 対象行のインデックス
   * @param value - 入力値
   */
  const handleStartCellInput = (index: number, value: string) => {
    const target = mappingStates.value[index];
    target.startCell = value.trim().toUpperCase();
    updateMappingFieldState(target, { validateValue: true, enforcePattern: true });
  };

  /**
   * フォーム全体を検証する。
   * @returns バリデーション結果
   */
  const validateDuplicateCells = (): boolean => {
    const cellCounts = new Map<string, number>();
    selectedMappings.value.forEach((field) => {
      if (!field.enabled || field.startCell === '') {
        return;
      }
      cellCounts.set(field.startCell, (cellCounts.get(field.startCell) ?? 0) + 1);
    });

    let hasDuplicate = false;
    mappingStates.value.forEach((field) => {
      const count = cellCounts.get(field.startCell) ?? 0;
      const state = fieldState[buildMappingFieldName(field.fieldKey)];
      if (field.enabled && field.startCell !== '' && count > 1) {
        hasDuplicate = true;
        state.isError.value = true;
        state.errorMessage.value = DUPLICATE_CELL_ERROR;
      } else if (state.errorMessage.value === DUPLICATE_CELL_ERROR) {
        state.isError.value = false;
        state.errorMessage.value = '';
      }
    });

    return !hasDuplicate;
  };

  /**
   * フォーム送信前の総合バリデーションを実行する。
   * @returns バリデーションを通過した場合にtrue
   */
  const validateForm = (): boolean => {
    formMessage.value = '';
    formMessageType.value = 'info';

    if (!hasUploadInfo.value) {
      formMessageType.value = 'error';
      formMessage.value = t('先にテンプレートファイルをアップロードしてください') as string;
      return false;
    }

    if (!validateMaxOrdersPerSheet()) {
      formMessageType.value = 'error';
      formMessage.value = t('1シートあたりの出力上限を正しく入力してください') as string;
      return false;
    }

    if (selectedMappings.value.length === 0) {
      formMessageType.value = 'error';
      formMessage.value = t('出力する項目を1件以上選択してください') as string;
      return false;
    }

    const mappingsValid = mappingStates.value.every((field) =>
      updateMappingFieldState(field, { validateValue: field.enabled, enforcePattern: field.enabled })
    );

    if (!mappingsValid) {
      formMessageType.value = 'error';
      formMessage.value = t('セル形式が正しくありません（例：A1, B10, AA10）') as string;
      return false;
    }

    if (!validateDuplicateCells()) {
      formMessageType.value = 'error';
      formMessage.value = DUPLICATE_CELL_ERROR;
      return false;
    }

    return true;
  };

  /**
   * テンプレート登録・更新リクエストを構築する。
   * @returns APIリクエストボディ
   */
  const buildRequest = (): OrderTemplateUpsertRequest => ({
    template: {
      templateId: templateId.value,
      fileOriginalName: fileOriginalName.value,
      storagePath: storagePath.value,
      maxOrdersPerSheet: maxOrdersPerSheet.value ?? 0,
    },
    mappings: selectedMappings.value.map((field) => ({
      fieldKey: field.fieldKey,
      startCell: field.startCell,
    })),
  });

  /**
   * 既存テンプレートの詳細を読み込みフォームに設定する。
   */
  const loadTemplateDetail = async (): Promise<void> => {
    if (!templateId.value) {
      return;
    }

    initializing.value = true;
    formMessage.value = '';
    formMessageType.value = 'info';

    const result = await diContainer.getOrderTemplateDetailUseCase.Execute(templateId.value);
    if (result.status.type === 'success') {
      const detail = result.status.data;
      maxOrdersPerSheetInput.value = detail.metadata.maxOrdersPerSheet ? String(detail.metadata.maxOrdersPerSheet) : '';
      fieldState[MAX_ORDERS_FIELD_NAME].setValue(maxOrdersPerSheetInput.value, false);
      fieldState[MAX_ORDERS_FIELD_NAME].isError.value = false;
      fieldState[MAX_ORDERS_FIELD_NAME].errorMessage.value = '';
      fileOriginalName.value = detail.metadata.fileOriginalName;
      storagePath.value = detail.metadata.storagePath;

      const mappingMap = new Map(detail.mappings.map((mapping) => [mapping.fieldKey, mapping]));
      mappingStates.value = ORDER_TEMPLATE_MAPPING_FIELDS.map((field) => {
        const mapping = mappingMap.get(field.fieldKey);
        return {
          fieldKey: field.fieldKey,
          label: field.label,
          startCell: mapping?.startCell ?? '',
          enabled: Boolean(mapping),
        };
      });

      mappingStates.value.forEach((field) => {
        updateMappingFieldState(field, { validateValue: false, enforcePattern: false });
      });
    } else {
      formMessageType.value = 'error';
      formMessage.value = result.status.error?.message ?? (t('テンプレート情報の取得に失敗しました') as string);
    }

    initializing.value = false;
  };

  /**
   * セッションストレージからドラフト情報を取得する。
   * @param id - テンプレートID
   * @returns ドラフト情報
   */
  const loadDraftFromStorage = (id: string): { templateId: string; fileName: string; storagePath: string } | null => {
    try {
      const raw = sessionStorage.getItem(`${TEMPLATE_DRAFT_PREFIX}${id}`);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { templateId: string; fileName: string; storagePath: string } | null;
      if (!parsed || parsed.templateId !== id) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  /**
   * セッションストレージからドラフト情報を削除する。
   * @param id - テンプレートID
   */
  const clearDraftFromStorage = (id: string) => {
    try {
      sessionStorage.removeItem(`${TEMPLATE_DRAFT_PREFIX}${id}`);
    } catch {
      // ignore
    }
  };

  /**
   * 新規登録向けにフォーム状態を初期化する。
   */
  const resetFormForCreate = () => {
    maxOrdersPerSheetInput.value = '20';
    fieldState[MAX_ORDERS_FIELD_NAME].setValue(maxOrdersPerSheetInput.value, false);
    fieldState[MAX_ORDERS_FIELD_NAME].isError.value = false;
    fieldState[MAX_ORDERS_FIELD_NAME].errorMessage.value = '';
    mappingStates.value = createDefaultMappingStates();
    mappingStates.value.forEach((field) => {
      updateMappingFieldState(field, { validateValue: false, enforcePattern: false });
    });
    formMessage.value = '';
    formMessageType.value = 'info';
    initializing.value = false;
  };

  /**
   * ルートクエリからテンプレートIDを同期する。
   */
  const syncRouteQueries = () => {
    templateId.value = typeof route.query.templateId === 'string' ? route.query.templateId : '';
  };

  /**
   * ルート状態に合わせてフォームを構築する。
   */
  const applyRouteState = async () => {
    syncRouteQueries();
    if (isEditMode.value && templateId.value) {
      await loadTemplateDetail();
    } else {
      resetFormForCreate();
      if (templateId.value) {
        const draft = loadDraftFromStorage(templateId.value);
        if (draft) {
          fileOriginalName.value = draft.fileName;
          storagePath.value = draft.storagePath;
        } else {
          formMessageType.value = 'error';
          formMessage.value = t('アップロード済みテンプレート情報が見つかりません。再度アップロードからやり直してください。') as string;
        }
      } else {
        formMessageType.value = 'error';
        formMessage.value = t('テンプレートIDが指定されていません。') as string;
      }
    }
  };

  watch(
    () => [route.name, route.query.templateId, route.query.fileName, route.query.storagePath],
    () => {
      void applyRouteState();
    },
    { immediate: true }
  );

  /**
   * フォーム送信を処理する。
   */
  const submit = async () => {
    if (!validateForm()) {
      return;
    }

    loading.value = true;
    formMessage.value = '';
    formMessageType.value = 'info';

    try {
      const request = buildRequest();
      const result = isEditMode.value
        ? await diContainer.updateOrderTemplateUseCase.Execute(templateId.value, request)
        : await diContainer.createOrderTemplateUseCase.Execute(request);
      if (result.status.type === 'success') {
        formMessageType.value = 'info';
        formMessage.value = isEditMode.value ? (t('テンプレートを更新しました') as string) : (t('テンプレートを登録しました') as string);
        if (!isEditMode.value && templateId.value) {
          clearDraftFromStorage(templateId.value);
        }
        await router.push({ name: 'order-template-list' });
        return;
      }

      formMessageType.value = 'error';
      formMessage.value = result.status.error?.message ?? (t('テンプレート登録に失敗しました') as string);
    } finally {
      loading.value = false;
    }
  };

  /**
   * フォームをキャンセルして一覧に戻る。
   */
  const cancel = async () => {
    await router.push({ name: 'order-template-list' });
  };

  return {
    templateId,
    fileOriginalName,
    storagePath,
    formMessage,
    formMessageType,
    loading,
    initializing,
    hasUploadInfo,
    isBusy,
    isEditMode,
    fieldState,
    mappingStates,
    maxOrdersFieldName: MAX_ORDERS_FIELD_NAME,
    buildMappingFieldName,
    handleMaxOrdersPerSheetInput,
    handleToggleField,
    handleStartCellInput,
    submit,
    cancel,
  };
};
